/**
 * Detail view for one eHungry order, opened from EhungryOrderCard.
 *
 * Shows what the legacy RushMyFood app's own Dispatch page shows — customer,
 * address, items with add-ons, the money breakdown, and First Delivery's
 * timeline — plus the one action that exists here: changing the status.
 *
 * That change is a write-through. It POSTs to the legacy app, which applies it
 * through its own Order model (so its cancellation audit trail keeps working)
 * and returns the saved row, which is what lands in the mirror. Nothing is
 * updated locally first: if that app rejects the change, this must not show it
 * as applied.
 *
 * The buttons are the same three the legacy Dispatch page offers, and no more.
 * There is no Accept / Mark Ready here because nobody on this side works these
 * orders — the food was made and delivered through another system.
 */
import React from 'react'
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography,
  Stack, CircularProgress, Alert, IconButton,
} from '@mui/material'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import CloseIcon from '@mui/icons-material/Close'
import OpenInNewIcon from '@mui/icons-material/OpenInNew'
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import { EHUNGRY_STATUS_CONFIG, EHUNGRY_TYPE_LABEL } from './EhungryOrderCard'
import { ops, FS, RADIUS, TONES, tone, tabular, focusRing, btn, dialogPaperSx, backdropSx } from './opsTokens'
import {
  StatusChip, Tag, FulfilmentTag, InitialsAvatar, ProviderMark, InfoGrid, InfoField, EtaTile,
  SectionTitle, ItemRow, ModLine, SumRow, TotalRow, DriverCard, DriverIdentity, ZoomablePhoto
} from './OpsUi'

const money = (n) => (n == null ? '—' : `$${Number(n).toFixed(2)}`)
// Eastern, the restaurants' clock — never the viewer's machine timezone.
const when = (s) => (s ? new Date(s).toLocaleString('en-US', { timeZone: 'America/New_York' }) : '—')

const TYPE_KIND = { PICKUP: 'pickup', DELIVERY: 'delivery', DINEIN: 'dinein' }

/** First Delivery's stages, in the order they happen. Only the ones that actually have a time are drawn. */
const COURIER_STEPS = [
  ['assignedAt', 'Driver assigned'],
  ['acceptedAt', 'Driver accepted'],
  ['arrivedOriginAt', 'Arrived at restaurant'],
  ['pickedUpAt', 'Picked up'],
  ['arrivedDropoffAt', 'Arrived at customer'],
  ['droppedOffAt', 'Delivered'],
  ['cancelledAt', 'Cancelled'],
]

// courierName: what to call the courier when the order doesn't name one — restaurants see RushMyFood, never First Delivery.
const EhungryOrderDetail = ({ order, onClose, onChangeStatus, changingStatus, error, courierName = 'First Delivery' }) => {
  if (!order) return null

  const config = EHUNGRY_STATUS_CONFIG[order.status] || { label: order.status, tone: 'neutral' }
  const items = order.items || []
  const courier = order.courier

  return (
    <Dialog
      open={!!order}
      onClose={onClose}
      maxWidth={false}
      fullWidth
      PaperProps={{ sx: { ...dialogPaperSx, width: 'calc(100% - 32px)', maxWidth: 720, m: 2, maxHeight: 'calc(100% - 32px)' } }}
      BackdropProps={{ sx: backdropSx }}
    >
      <DialogTitle component="div" sx={{ px: { xs: 2, md: 3 }, py: 2, borderBottom: `1px solid ${ops.border}` }}>
        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" gap={1.5}>
          <Box minWidth={0}>
            <Typography component="h2" sx={{ fontSize: FS.xl, fontWeight: 600, color: ops.text, lineHeight: 1.3, ...tabular }}>
              #{order.remoteId}
            </Typography>
            <Stack direction="row" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ mt: 0.75 }}>
              <StatusChip toneName={config.tone} label={config.label} />
              <Tag icon={<StorefrontOutlinedIcon />}>RushMyFood (old)</Tag>
              <FulfilmentTag kind={TYPE_KIND[order.orderType]}>{EHUNGRY_TYPE_LABEL[order.orderType] || order.orderType}</FulfilmentTag>
            </Stack>
          </Box>
          <IconButton
            aria-label="Close"
            onClick={onClose}
            size="small"
            sx={{ width: 36, height: 36, borderRadius: RADIUS.button, color: ops.muted, flexShrink: 0, '&:hover': { bgcolor: ops.sunken, color: ops.text }, ...focusRing }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </Stack>
      </DialogTitle>

      <DialogContent sx={{ px: { xs: 2, md: 3 }, py: '20px !important' }}>
        <Stack gap={2.5}>
          {error && <Alert severity="error" sx={{ borderRadius: RADIUS.button }}>{error}</Alert>}

          <InfoGrid>
            <InfoField label="Customer">
              <Box display="flex" alignItems="center" gap={1} minWidth={0}>
                <InitialsAvatar name={order.customerName} size={28} />
                <Box minWidth={0}>
                  <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{order.customerName || '—'}</Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, fontSize: FS.sm, fontWeight: 400, color: ops.muted, ...tabular }}>
                    {order.customerPhone && <PhoneOutlinedIcon sx={{ fontSize: 14 }} />}
                    {order.customerPhone || 'no phone'}
                  </Box>
                </Box>
              </Box>
            </InfoField>
            <InfoField label="Restaurant" icon={<StorefrontOutlinedIcon />}>{order.restaurantName || '—'}</InfoField>
            <InfoField label="Placed"><Box component="span" sx={tabular}>{when(order.placedAt)}</Box></InfoField>
            {order.readyAt && <InfoField label="Ready at"><Box component="span" sx={tabular}>{when(order.readyAt)}</Box></InfoField>}

            {order.deliveryAddress && (
              <InfoField label="Delivery address" icon={<PlaceOutlinedIcon />} sx={{ gridColumn: '1 / -1' }}>
                {order.deliveryAddress}
                {order.deliveryNote && (
                  <Box sx={{ fontSize: FS.sm, fontWeight: 400, color: ops.muted, mt: 0.25 }}>{order.deliveryNote}</Box>
                )}
              </InfoField>
            )}
            {order.specialInstructions && <InfoField label="Instructions" sx={{ gridColumn: '1 / -1' }}>{order.specialInstructions}</InfoField>}
            {order.notesToDriver && <InfoField label="Notes to driver" sx={{ gridColumn: '1 / -1' }}>{order.notesToDriver}</InfoField>}
          </InfoGrid>

          <Box>
            <SectionTitle>Items</SectionTitle>
            <Box sx={{ borderTop: `1px solid ${ops.border}` }}>
              {items.length === 0 && <Typography sx={{ fontSize: FS.md, color: ops.muted, py: 1.25 }}>No items recorded</Typography>}
              {items.map((item, idx) => (
                <ItemRow
                  key={idx}
                  qty={`${item.quantity}x`}
                  last={idx === items.length - 1}
                  price={<Typography sx={{ fontSize: FS.md, fontWeight: 600, color: ops.text, ...tabular }}>{money(item.totalPrice)}</Typography>}
                  extra={(item.addons || []).length > 0 || item.specialInstructions ? (
                    <>
                      {(item.addons || []).map((addon, i) => (
                        <Box key={i} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.5 }}>
                          {/* "Plate Sides (Korean BBQ Wings)" — the group alone
                              says nothing about what was ordered. */}
                          <ModLine indent={1}>
                            + {addon.choice ? `${addon.name} (${addon.choice})` : addon.name}
                            {addon.portion ? ` — ${addon.portion}` : ''}
                          </ModLine>
                          <ModLine>{money(addon.price)}</ModLine>
                        </Box>
                      ))}
                      {item.specialInstructions && (
                        <ModLine indent={1} italic>
                          {item.specialInstructions}
                        </ModLine>
                      )}
                    </>
                  ) : null}
                >
                  <Typography sx={{ fontSize: FS.md, fontWeight: 500, color: ops.text }}>
                    {item.name}
                    {/* The size is part of the item, not a footnote — a Cup
                        and a Bowl are different orders. */}
                    {item.variant ? ` [${item.variant}]` : ''}
                    {item.forGuest ? ` (${item.forGuest})` : ''}
                  </Typography>
                </ItemRow>
              ))}
            </Box>
          </Box>

          <Box p={2} sx={{ bgcolor: ops.subtle, borderRadius: RADIUS.tile, border: `1px solid ${ops.border}` }}>
            <SumRow label="Subtotal" value={money(order.subtotal)} />
            {order.discount > 0 && <SumRow toneName="emerald" label="Discount" value={`-${money(order.discount)}`} />}
            {order.couponAmount > 0 && <SumRow toneName="emerald" label={order.couponName || 'Coupon'} value={`-${money(order.couponAmount)}`} />}
            <SumRow label={order.taxLabel || 'Tax'} value={money(order.tax)} />
            {order.deliveryCharge > 0 && <SumRow label="Delivery charge" value={money(order.deliveryCharge)} />}
            {order.additionalFee > 0 && <SumRow label="Service fee" value={money(order.additionalFee)} />}
            {order.tip > 0 && <SumRow label="Tip" value={money(order.tip)} />}
            <TotalRow label="Total" value={money(order.total)} />
            {order.paymentDescription && (
              /* Stored as HTML by the legacy app ("…5369<br />Name: …"). */
              <Typography sx={{ fontSize: FS.xs, color: ops.muted, whiteSpace: 'pre-line', mt: 1, ...tabular }}>
                {order.paymentDescription.replace(/<br\s*\/?>/gi, '\n').replace(/<[^>]+>/g, '')}
              </Typography>
            )}
          </Box>

          {/* Only drawn when First Delivery actually carried this order. A pickup
              gets no delivery section rather than an empty one. */}
          {courier && (
            <DriverCard label="Delivery">
              <DriverIdentity
                avatar={<ProviderMark name={courier.provider || courierName} size={44} />}
                name={courier.provider || courierName}
                sub={
                  <>
                    {(courier.status || '').replace(/_/g, ' ') || 'no status'}
                    {courier.fee != null ? ` · fee ${money(courier.fee)}` : ''}
                  </>
                }
              />
              {/* Driver, tracking and proof — what a marketplace order shows. */}
              {(courier.driverName || courier.driverPhone) && (
                <Box sx={{ pt: 1.25, borderTop: `1px solid ${ops.border}` }}>
                  <DriverIdentity
                    avatar={<InitialsAvatar name={courier.driverName || '?'} src={courier.driverPhoto || undefined} size={44} />}
                    name={
                      <>
                        {courier.driverName || 'Driver Assigned'}
                        {courier.driverTransport ? ` · ${courier.driverTransport}` : ''}
                      </>
                    }
                    sub={courier.driverPhone && (
                      <Box
                        component="a"
                        href={`tel:${courier.driverPhone}`}
                        sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, color: ops.text2, textDecoration: 'none', fontWeight: 500, '&:hover': { color: ops.text, textDecoration: 'underline' }, ...focusRing }}
                      >
                        <PhoneOutlinedIcon sx={{ fontSize: 14 }} />
                        {courier.driverPhone}
                      </Box>
                    )}
                  />
                </Box>
              )}
              {(courier.pickupEstimate || courier.dropoffEstimate) && (
                <Stack direction={{ xs: 'column', sm: 'row' }} gap={1}>
                  {courier.pickupEstimate && <EtaTile label="Pickup ETA" value={when(courier.pickupEstimate)} />}
                  {courier.dropoffEstimate && <EtaTile label="Dropoff ETA" value={when(courier.dropoffEstimate)} />}
                </Stack>
              )}
              {(courier.trackerLink || courier.dspTrackerLink) && (
                <Stack direction="row" gap={1} flexWrap="wrap">
                  {courier.trackerLink && (
                    <Button href={courier.trackerLink} target="_blank" rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
                      sx={{ ...btn.secondary, flex: 1, minWidth: 120 }}>
                      Tracker 1
                    </Button>
                  )}
                  {courier.dspTrackerLink && (
                    <Button href={courier.dspTrackerLink} target="_blank" rel="noopener noreferrer"
                      endIcon={<OpenInNewIcon sx={{ fontSize: '15px !important' }} />}
                      sx={{ ...btn.secondary, flex: 1, minWidth: 120 }}>
                      Tracker 2
                    </Button>
                  )}
                </Stack>
              )}
              {courier.dropoffPhoto ? (
                <ZoomablePhoto src={courier.dropoffPhoto} alt="Proof of delivery" title="PROOF OF DELIVERY" width={160} />
              ) : (courier.droppedOffAt || courier.status === 'completed') ? (
                <Typography sx={{ fontSize: FS.sm, color: ops.muted }}>
                  No delivery photo was provided for this order
                </Typography>
              ) : null}
              {COURIER_STEPS.some(([key]) => courier[key]) && (
                <Box component="ol" sx={{ listStyle: 'none', m: 0, p: 0 }}>
                  {COURIER_STEPS.filter(([key]) => courier[key]).map(([key, label], i, shown) => (
                    <Box component="li" key={key} sx={{ position: 'relative', display: 'flex', alignItems: 'baseline', gap: 1.25, pl: 2.25, pb: i === shown.length - 1 ? 0 : 1 }}>
                      <Box aria-hidden sx={{ position: 'absolute', left: 3, top: 6, width: 8, height: 8, borderRadius: '50%', bgcolor: key === 'cancelledAt' ? TONES.rose.dot : TONES.emerald.dot }} />
                      {i < shown.length - 1 && (
                        <Box aria-hidden sx={{ position: 'absolute', left: 6, top: 16, bottom: 0, width: 2, bgcolor: ops.border, borderRadius: 1 }} />
                      )}
                      <Typography sx={{ flex: 1, fontSize: FS.sm, color: ops.text2 }}>{label}</Typography>
                      <Typography sx={{ fontSize: FS.sm, color: ops.muted, ...tabular }}>{when(courier[key])}</Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </DriverCard>
          )}

          {order.assignedCourierName && (
            <SumRow label="Assigned courier" value={order.assignedCourierName} />
          )}
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: { xs: 2, md: 3 }, py: 2, borderTop: `1px solid ${ops.border}`, justifyContent: 'space-between', flexWrap: 'wrap', gap: 1 }}>
        <Stack direction="row" gap={1} flexWrap="wrap">
          {/* A finished order stays finished — same as the tablet, which hides these once an order is completed or cancelled. */}
          {order.status === 'pending' && Object.keys(EHUNGRY_STATUS_CONFIG)
            .filter((s) => s !== order.status)
            .map((s) => (
              <Button
                key={s}
                size="small"
                disabled={changingStatus}
                onClick={() => onChangeStatus && onChangeStatus(order, s)}
                sx={{
                  ...btn.secondary,
                  px: 1.75,
                  color: tone(EHUNGRY_STATUS_CONFIG[s].tone).fg,
                  borderColor: ops.borderStrong,
                  '&:hover': { bgcolor: tone(EHUNGRY_STATUS_CONFIG[s].tone).bg, borderColor: ops.borderStrong }
                }}
              >
                Mark {EHUNGRY_STATUS_CONFIG[s].label.toLowerCase()}
              </Button>
            ))}
          {changingStatus && <CircularProgress size={18} sx={{ alignSelf: 'center', color: ops.muted }} />}
        </Stack>
        <Button onClick={onClose} sx={{ ...btn.secondary, px: 2 }}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}

export default EhungryOrderDetail
